import React, { useState, useEffect, useCallback } from "react";
import { useSwipeable } from "react-swipeable";
import { useNavigate } from "react-router-dom";
import "../index.css"; 

const HeroSection = ({ apiHerosImage }) => {
  const [images,       setImages]       = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating,  setIsAnimating]  = useState(false);
  const navigate = useNavigate();

  /* ── Load images ── */
  useEffect(() => {
    if (!apiHerosImage || !Array.isArray(apiHerosImage)) return;
    setImages(apiHerosImage.map((item) => ({ src: item.image, url: item.url })));
  }, [apiHerosImage]);

  /* ── Auto-advance ── */
  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 400);
  }, [images.length, isAnimating]);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 400);
  }, [images.length, isAnimating]);

  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(handleNext, 5000);
    return () => clearInterval(interval);
  }, [images.length, handleNext]);

  /* ── Swipe ── */
  const swipeHandlers = useSwipeable({
    onSwipedLeft:  handleNext,
    onSwipedRight: handlePrev,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    trackMouse: true,
  });

  /* ── Click ── */
  const handleImageClick = () => {
    const current = images[currentIndex];
    if (!current?.url) return;
    current.url.startsWith("http")
      ? (window.location.href = current.url)
      : navigate(current.url);
  };

  if (!images.length) {
    return (
      <div className="relative w-full h-[220px] sm:h-[320px] lg:h-[460px] bg-[#162c2a] flex items-center justify-center overflow-hidden rounded-2xl">
        {/* Plus pattern shimmer */}
        <div
          className="absolute inset-0 opacity-[0.06] animate-pulse"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize:   "40px 40px",
          }}
        />
        <div className="relative flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#5fbdb3]/20 border border-[#5fbdb3]/30 flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <p className="text-[13px] font-semibold text-white/40 font-poppins">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div
      {...swipeHandlers}
      className="relative w-full overflow-hidden rounded-b-2xl shadow-[0_8px_40px_rgba(22,44,42,0.2)] group"
      style={{ cursor: images[currentIndex]?.url ? "pointer" : "default" }}
    >
      {/* ── Image ── */}
      <div
        className="relative w-full h-[220px] sm:h-[320px] md:h-[380px] lg:h-[460px] xl:h-[500px]"
        onClick={handleImageClick}
      >
        <img
          key={currentIndex}
          src={images[currentIndex]?.src}
          alt={`Banner ${currentIndex + 1}`}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ animation: "heroFade 0.4s ease both" }}
          onError={(e) => { e.target.onerror = null; e.target.src = "/default.svg"; }}
        />

        {/* Bottom gradient for dots readability */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0a1715]/60 to-transparent pointer-events-none" />

        {/* Top-right slide counter badge */}
        <div className="absolute top-3 right-3 bg-[#0a1715]/60 backdrop-blur-sm text-white font-poppins text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/10">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* ── Prev arrow ── */}
      <button
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        aria-label="Previous slide"
        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-20
          w-9 h-9 sm:w-11 sm:h-11 rounded-xl
          bg-[#0a1715]/55 backdrop-blur-sm border border-white/15
          flex items-center justify-center text-white
          opacity-0 group-hover:opacity-100 translate-x-[-6px] group-hover:translate-x-0
          transition-all duration-250 hover:bg-[#1e4a46]/80 hover:border-[#5fbdb3]/50
          hover:shadow-[0_4px_14px_rgba(95,189,179,0.35)]
          hidden sm:flex"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 5l-7 7 7 7"/>
        </svg>
      </button>

      {/* ── Next arrow ── */}
      <button
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        aria-label="Next slide"
        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-20
          w-9 h-9 sm:w-11 sm:h-11 rounded-xl
          bg-[#0a1715]/55 backdrop-blur-sm border border-white/15
          flex items-center justify-center text-white
          opacity-0 group-hover:opacity-100 translate-x-[6px] group-hover:translate-x-0
          transition-all duration-250 hover:bg-[#1e4a46]/80 hover:border-[#5fbdb3]/50
          hover:shadow-[0_4px_14px_rgba(95,189,179,0.35)]
          hidden sm:flex"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </button>

      {/* ── Progress bar ── */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10 z-20">
        <div
          className="h-full bg-gradient-to-r from-[#ff601e] to-[#5fbdb3] rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / images.length) * 100}%` }}
        />
      </div>

      {/* ── Dots ── */}
      <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-1.5 z-20">
        {images.map((_, index) => (
          <button
            key={index}
            aria-label={`Go to slide ${index + 1}`}
            onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
            className={`rounded-full transition-all duration-300 ${
              index === currentIndex
                ? "w-6 h-2 bg-white shadow-[0_2px_6px_rgba(255,255,255,0.4)]"
                : "w-2 h-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

    </div>
  );
};

export default HeroSection;